import React, { memo, useState } from 'react';
import PropTypes from 'prop-types';
import { Lock } from 'lucide-react';

import Bio from '../PortfolioAbout/components/ChildComponents/Bio/Bio';
import Skills from '../PortfolioAbout/components/ChildComponents/Skills/Skills';
import Experience from '../PortfolioAbout/components/ChildComponents/Experience/Experience';
import Language from '../PortfolioAbout/components/ChildComponents/Language/Language';
import IndustryExperience from '../PortfolioAbout/components/ChildComponents/IndustryExperience/IndustryExperience';
import Diversity from '../PortfolioAbout/components/ChildComponents/Diversity/Diversity';
import WorkExperience from '../PortfolioAbout/components/ChildComponents/WorkExperience/WorkExperience';
import Education from '../PortfolioAbout/components/ChildComponents/Education/Education';
import Qualification from '../PortfolioAbout/components/ChildComponents/Qualification/Qualification';
import useCareerEntries from '../PortfolioAbout/components/ChildComponents/useCareerEntries';
import PortfolioAdverts from '../PortfolioAbout/components/ChildComponents/Adverts/PortfolioAdverts';
import PortfolioProjects from '../PortfolioProjects/PortfolioProjects';
import PortfolioTestimonials from '../PortfolioTestimonials/PortfolioTestimonials';
import { HireBand, MoreExperts } from '../PortfolioExtras/PortfolioExtras';
import hocStyles from '../PortfolioAbout/components/hoc/hoc.module.scss';
import {
  getEducationEntries,
  getQualificationEntries,
  isFreelanceGigSkill,
} from 'helpers/portfolioLayout';
import styles from './PortfolioLayout.module.scss';

// Each section is rendered once. Its slot class sets where it sits in the
// narrow or wide column, and its CSS order in one-column and phone layouts.
const Slot = ({ name, children }) => (
  <div className={`${styles.slot} ${styles[name]}`}>{children}</div>
);

Slot.propTypes = {
  name: PropTypes.string.isRequired,
  children: PropTypes.node,
};

const SkillsAndLanguages = ({ user, isOwn, loggedInUser }) => {
  const visibleSkills = isOwn
    ? user.skills
    : user.skills.filter((skill) => !isFreelanceGigSkill(skill));
  const visibleLanguages = isOwn
    ? user.language
    : user.language.filter((language) => language.experience > 0);

  if (!isOwn && visibleSkills.length === 0 && visibleLanguages.length === 0) {
    return null;
  }

  return (
    <div className={hocStyles.panel} id="portfolio-skills-card">
      <Skills
        variant="lead"
        title="Skills"
        count={visibleSkills.length}
        user={user}
        userData={user.skills}
        updateKey="skills"
        isOwner={isOwn}
        isEmpty={visibleSkills.length === 0}
        placeholder="Add skills to improve your ranking and attract new clients."
        addLabel="Add skills"
        loggedInUser={loggedInUser}
      />
      {isOwn && user.skills.length > 0 && (
        <Experience
          variant="section"
          title="Skill levels"
          user={user}
          userData={user.skills}
          updateKey="skills"
          componentID="experience" // Used to identify components that have a duplicated updateKey
          isOwner={isOwn}
          isEmpty={false}
          loggedInUser={loggedInUser}
        />
      )}
      {(isOwn || visibleLanguages.length > 0) && (
        <>
          {(isOwn || visibleSkills.length > 0) && (
            <div className={styles.divider} />
          )}
          <Language
            variant="section"
            title="Languages"
            user={user}
            userData={user.language}
            updateKey="language"
            placeholder="Add the languages you speak."
            addLabel="Add languages"
            isEmpty={visibleLanguages.length === 0}
            isOwner={isOwn}
          />
        </>
      )}
    </div>
  );
};

SkillsAndLanguages.propTypes = {
  user: PropTypes.object.isRequired,
  isOwn: PropTypes.bool,
  loggedInUser: PropTypes.object,
};

const PortfolioLayout = ({
  twoColumns,
  user,
  loggedInUser,
  isOwn,
  isPreviewing,
  credits,
  creditsTotal,
  projectCount,
  loadingCredits,
  moreCredits,
  isReorderingCredits,
  onReorderCredit,
  onLoadMoreCredits,
  testimonials,
  testimonialTotal,
  onTestimonials,
  onReport,
  sentReport,
  onEmailActivate,
  showMoreAbout,
  onShowMoreAbout,
  partners,
  loadedPartners,
  onContact,
}) => {
  const educationEntries = getEducationEntries(user.education);
  const qualificationEntries = getQualificationEntries(user.education);
  // Education and certifications both save user.education, so they share
  // one editor and a pending delete in one can't be lost by a save in the
  // other.
  const educationEditor = useCareerEntries({
    user,
    entries: user.education,
    field: 'education',
  });
  // Each dialog links to the other section's Add dialog.
  const [addRequest, setAddRequest] = useState({ section: null, at: 0 });
  const requestAdd = (section) => setAddRequest({ section, at: Date.now() });
  const addRequestFor = (section) =>
    addRequest.section === section ? addRequest.at : 0;

  const narrow = (
    <>
      <Slot name="about">
        <Bio
          showMore={showMoreAbout}
          showShowMoreButton={user.bio.length > 600}
          user={user}
          isOwn={isOwn}
          onShowMore={onShowMoreAbout}
          onReport={onReport}
          sentReport={sentReport}
          onEmailActivate={onEmailActivate}
          projectCount={projectCount}
        />
      </Slot>
      <Slot name="skills">
        <SkillsAndLanguages
          user={user}
          isOwn={isOwn}
          loggedInUser={loggedInUser}
        />
      </Slot>
      <Slot name="industry">
        <IndustryExperience
          title="Industry experience"
          user={user}
          userData={user.sectors.map((sector) => sector.name)}
          updateKey="sectors"
          isOwner={isOwn}
          isEmpty={user.sectors.length === 0}
          placeholder="Improve your ranking by adding your industry experience."
          addLabel="Add industries"
        />
      </Slot>
      {isOwn && (
        <Slot name="diversity">
          <Diversity
            title="Diversity & inclusion"
            titleIcon={
              <Lock
                size={16}
                strokeWidth={2}
                color="#6B7785"
                aria-label="Private"
              />
            }
            user={loggedInUser}
            userData={loggedInUser.diversity.map((diversity) => diversity.name)}
            updateKey="diversity"
            isOwner={isOwn}
            // Never public, so it is not a "hidden until added" section.
            isEmpty={false}
            permanentInformation={{
              body: (
                <>
                  <span className={styles.privateNote}>
                    Only you can see this. It is never shown on your public
                    portfolio.
                  </span>
                  {loggedInUser.diversity.length === 0 && (
                    <p className={styles.diversityPrompt}>
                      Clients may have a preference for hiring from an
                      underrepresented group. Use the pencil to select any that
                      apply to you.
                    </p>
                  )}
                </>
              ),
            }}
          />
        </Slot>
      )}
      {/* Pro and Premium portfolios don't link clients to other experts. */}
      {!isOwn && !user.pro && (
        <Slot name="moreExperts">
          <MoreExperts user={user} showPlanNote={isPreviewing} />
        </Slot>
      )}
    </>
  );

  const wide = (
    <>
      {isOwn && loadedPartners && (
        <Slot name="partner">
          <PortfolioAdverts
            loadedPartners={loadedPartners}
            partners={partners}
            userLocation={user.location.text}
          />
        </Slot>
      )}
      <Slot name="projects">
        <PortfolioProjects
          credits={credits}
          total={creditsTotal}
          isOwn={isOwn}
          isReorderingCredits={isReorderingCredits}
          loadingCredits={loadingCredits}
          moreCredits={moreCredits}
          onLoadMore={onLoadMoreCredits}
          onReorderCredit={onReorderCredit}
          user={user}
        />
      </Slot>
      <Slot name="work">
        <WorkExperience
          user={user}
          userData={user.workexperience}
          isOwn={isOwn}
          isEmpty={user.workexperience.length === 0}
          placeholder="Add your work history so clients can see where you've worked."
        />
      </Slot>
      <Slot name="education">
        <Education
          user={user}
          editor={educationEditor}
          isOwn={isOwn}
          isEmpty={educationEntries.length === 0}
          placeholder="Add your degrees and courses."
          addRequest={addRequestFor('education')}
          onAddQualification={() => requestAdd('qualifications')}
        />
      </Slot>
      <Slot name="qualifications">
        <Qualification
          user={user}
          editor={educationEditor}
          isOwn={isOwn}
          isEmpty={qualificationEntries.length === 0}
          placeholder="Industry certifications, licenses and awards you’ve won. Degrees and courses go in Education."
          addRequest={addRequestFor('qualifications')}
          onAddEducation={() => requestAdd('education')}
        />
      </Slot>
      <Slot name="testimonials">
        <PortfolioTestimonials
          testimonials={testimonials}
          total={testimonialTotal}
          user={user}
          isOwn={isOwn}
          onOpen={onTestimonials}
        />
      </Slot>
      {!isOwn && (
        <Slot name="hire">
          <HireBand user={user} onContact={onContact} />
        </Slot>
      )}
    </>
  );

  return (
    <div
      className={`${styles.layout} ${
        twoColumns
          ? styles.twoColumns
          : `${styles.oneColumn} portfolio-one-column`
      }`}
    >
      <div className={`${styles.column} ${styles.narrowColumn}`}>{narrow}</div>
      <div className={`${styles.column} ${styles.wideColumn}`}>{wide}</div>
    </div>
  );
};

PortfolioLayout.propTypes = {
  twoColumns: PropTypes.bool,
  user: PropTypes.object.isRequired,
  loggedInUser: PropTypes.object,
  isOwn: PropTypes.bool,
  isPreviewing: PropTypes.bool,
  credits: PropTypes.array,
  creditsTotal: PropTypes.number,
  // Undefined until the first credits page loads.
  projectCount: PropTypes.number,
  loadingCredits: PropTypes.bool,
  moreCredits: PropTypes.bool,
  isReorderingCredits: PropTypes.bool,
  onReorderCredit: PropTypes.func,
  onLoadMoreCredits: PropTypes.func,
  testimonials: PropTypes.array,
  testimonialTotal: PropTypes.number,
  onTestimonials: PropTypes.func,
  onReport: PropTypes.func,
  sentReport: PropTypes.bool,
  onEmailActivate: PropTypes.func,
  showMoreAbout: PropTypes.bool,
  onShowMoreAbout: PropTypes.func,
  partners: PropTypes.array,
  loadedPartners: PropTypes.bool,
  onContact: PropTypes.func,
};

export default memo(PortfolioLayout);
